import React, { FC, useEffect, useState } from "react";
import {
  Modal,
  Button,
  Typography,
  message,
  Spin
} from "antd";
import {
  DownloadOutlined
} from '@ant-design/icons';
import { AnalysisResults } from '../utils/types';
import { BaseUrl } from '../utils/network';

const { Text } = Typography;

interface AnalysisResultsModalProps {
  visible: boolean;
  onClose: () => void;
  analysisResults: AnalysisResults;
  caseNumber: number;
  pdfUrl?: string;
  pdfFilename?: string;
}

const AnalysisResultsModal: FC<AnalysisResultsModalProps> = ({
  visible,
  onClose,
  analysisResults,
  caseNumber,
  pdfUrl,
  pdfFilename
}) => {
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string>('');
  const [loadingPdf, setLoadingPdf] = useState(false);

  // Debug logging
  console.log('AnalysisResultsModal - pdfUrl:', pdfUrl);
  console.log('AnalysisResultsModal - pdfFilename:', pdfFilename);

  const handleDownloadPDF = async () => {
    if (!pdfUrl) {
      message.error('PDF report is not available');
      return;
    }

    try {
      // Get the base URL from network config
      const baseUrl = BaseUrl.replace('/api/', ''); // Remove /api/ from base URL
      const fullUrl = pdfUrl.startsWith('http') ? pdfUrl : `${baseUrl}${pdfUrl}`;

      // Fetch the file with proper headers
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('employeepro_token')}` || '',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the blob
      const blob = await response.blob();

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = pdfFilename || `case_${caseNumber}_analysis.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      window.URL.revokeObjectURL(downloadUrl);

      message.success('PDF report downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      message.error('Failed to download PDF report. Please try again.');
    }
  };

  // Fetch and prepare PDF for viewing
  const loadPdfForViewing = async () => {
    if (!pdfUrl) return;

    setLoadingPdf(true);
    try {
      const baseUrl = BaseUrl.replace('/api/', '');
      const fullUrl = pdfUrl.startsWith('http') ? pdfUrl : `${baseUrl}${pdfUrl}`;

      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}` || '',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      setPdfBlobUrl(blobUrl);
      setShowPdfViewer(true);
    } catch (error) {
      console.error('Error loading PDF:', error);
      message.error('Failed to load PDF. Click Download Report to save it.');
    } finally {
      setLoadingPdf(false);
    }
  };

  // Auto-open PDF viewer when modal becomes visible
  useEffect(() => {
    if (visible) {
      if (pdfUrl) {
        loadPdfForViewing();
        return () => {
          if (pdfBlobUrl) {
            window.URL.revokeObjectURL(pdfBlobUrl);
          }
        };
      } else {
        // If there's no PDF, just close and redirect
        onClose();
      }
    } else {
      // Close PDF viewer and clean up when results modal closes
      setShowPdfViewer(false);
      if (pdfBlobUrl) {
        window.URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, pdfUrl]);

  return (
    <>

      {/* PDF Viewer Modal */}
      {showPdfViewer && (
        <Modal
          title={`Case #${caseNumber} Analysis Report`}
          open={showPdfViewer}
          onCancel={() => {
            setShowPdfViewer(false);
            if (pdfBlobUrl) {
              window.URL.revokeObjectURL(pdfBlobUrl);
              setPdfBlobUrl('');
            }
            onClose();
          }}
          footer={[
            <Button
              key="download"
              icon={<DownloadOutlined />}
              onClick={handleDownloadPDF}
            >
              Download PDF
            </Button>,
            <Button
              key="close"
              type="primary"
              onClick={() => {
                setShowPdfViewer(false);
                if (pdfBlobUrl) {
                  window.URL.revokeObjectURL(pdfBlobUrl);
                  setPdfBlobUrl('');
                }
                onClose();
              }}
            >
              Close
            </Button>
          ]}
          width="90%"
          style={{ top: 20 }}
          bodyStyle={{ height: 'calc(100vh - 200px)', padding: 0 }}
          destroyOnClose
        >
          {loadingPdf ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Spin size="large" tip="Loading PDF..." />
            </div>
          ) : pdfBlobUrl ? (
            <iframe
              src={pdfBlobUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              title={`Case ${caseNumber} Report`}
            />
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Text>Failed to load PDF. Please use the Download button.</Text>
            </div>
          )}
        </Modal>
      )}
    </>
  );
};

export default AnalysisResultsModal;
